import { useState, useEffect, useMemo } from "react";
import { Drawer } from "../../shared";
import { CustomDropdown } from "../../atoms";
import { useAssignNewInstallMutation } from "../../../store";
import useAuth from "../../../Hooks/useAuth";
import { useAlert } from "../../../context/AlertContext";
import useDrawerTransition from "../../../Hooks/useDrawerTransition";

// Goz sayisi secici — chip stili kareler. disabledAfter ile belirli
// degerden sonrasi pasiflestirilir (Cay/Filtre secimine gore Turk
// Kahvesi'nin uust limiti).
function PotCountSelector({ label, value, onChange, options, disabledAfter }) {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-700">
        {label}
      </label>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const isActive = Number(value) === opt;
          const isDisabled =
            typeof disabledAfter === "number" && opt > disabledAfter;
          return (
            <button
              key={opt}
              type="button"
              disabled={isDisabled}
              onClick={() => onChange(opt)}
              className={`min-w-[44px] h-11 px-3 rounded-xl border text-sm font-semibold transition-all
                ${
                  isDisabled
                    ? "border-input bg-input/30 text-mainSecond/30 cursor-not-allowed"
                    : isActive
                      ? "border-main bg-main text-new shadow-sm"
                      : "border-input bg-white text-main hover:border-main/40 hover:shadow-sm"
                }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ttyS*  — standart UART (cogu Android tablette).
// ttyHS* — high-speed UART (bazi tablet modellerinde, orn. OrderPAD).
// ttyUSB* — USB-serial dongle (yaygin USB-RS232 adaptorler).
// Saha cihazlari farkli kombinasyonlarda kullaniyor; tum yaygin
// portlari listeye ekliyoruz.
const SERIAL_PORTS = [
  "/dev/ttyS0",
  "/dev/ttyS1",
  "/dev/ttyS2",
  "/dev/ttyS3",
  "/dev/ttyHS0",
  "/dev/ttyHS1",
  "/dev/ttyHS2",
  "/dev/ttyHS3",
  "/dev/ttyUSB0",
  "/dev/ttyUSB1",
];
const BAUD_RATES = [9600, 19200, 38400, 57600, 115200];

const PORT_OPTIONS = SERIAL_PORTS.map((p) => ({ id: p, label: p }));
const BAUD_OPTIONS = BAUD_RATES.map((b) => ({ id: b, label: String(b) }));

// machineName göz sayilarindan otomatik turetilir.
// BeverageMachine `machineName.includes(serCom.machineType)` ile port acmaya
// karar verdigi icin "Çayset" / "Fincanda" kelimelerinin gecmesi sart.
function deriveMachineName(potTea, potCoffee) {
  const tea = Number(potTea) > 0;
  const coffee = Number(potCoffee) > 0;
  if (tea && coffee) return "Çayset Fincanda";
  if (tea) return "Çayset";
  if (coffee) return "Fincanda";
  return "";
}

// Cay/Filtre goz sayisina gore Turk Kahvesi maksimum goz hesabi —
// donanim slot kisitlamasi. Cay arttikca Turk Kahvesi'ne ayrilabilecek
// alan azalir:
//   cay=0 → turk max 6 (tam alan)
//   cay=1 → turk max 3
//   cay=2 → turk max 3
//   cay=3 → turk max 0 (turk hic olamaz)
// Eski kart setli Turk kahvesi makinesi (4G karti, tempClientId "OKC_")
// yalniz 2 ya da 4 gozlu; Cay/Filtre gozu yok (WS#346).
const OKC_POT_OPTIONS = [2, 4];
function isOldCardSet(pending) {
  return String(pending?.tempClientId || "").startsWith("OKC_");
}

function getTurkishMax(tea) {
  const t = Number(tea) || 0;
  if (t >= 3) return 0;
  if (t >= 1) return 3;
  return 6;
}

/**
 * Yeni Kurulum drawer'ı — admin makine bilgilerini doldurup onaylar.
 * Cihaz default bayserNo="1" (uretim) firmasinda olusur; firma secimi
 * yapilmaz — distribution ayri akista yapilacak.
 * Server otomatik serial üretir + SERIAL_ASSIGNED publish eder.
 */
export default function NewInstallDrawer({ open, pending: pendingProp, onClose, onSuccess }) {
  // Kapanış animasyonu boyunca bekleyen cihaz kaydı dondurulur.
  const { mounted, data: pending } = useDrawerTransition(open, pendingProp);
  const { auth } = useAuth();
  const { showSuccess, showError } = useAlert();
  const [assign, { isLoading }] = useAssignNewInstallMutation();

  const [potTea, setPotTea] = useState(0);
  const [potCoffee, setPotCoffee] = useState(0);
  const [hasGrinder, setHasGrinder] = useState(false);
  const [teaPort, setTeaPort] = useState(PORT_OPTIONS[0]);
  const [teaBaud, setTeaBaud] = useState(
    BAUD_OPTIONS.find((b) => b.id === 115200),
  );
  const [coffeePort, setCoffeePort] = useState(PORT_OPTIONS[0]);
  const [coffeeBaud, setCoffeeBaud] = useState(
    BAUD_OPTIONS.find((b) => b.id === 19200),
  );

  const oldCardSet = isOldCardSet(pending);

  const machineName = useMemo(
    () => deriveMachineName(potTea, potCoffee),
    [potTea, potCoffee],
  );

  // Cay degisince Turk Kahvesi max'i da degisir; mevcut deger yeni
  // max'i asarsa otomatik kirpilir (ornek: turk=5 iken cay=2 secilirse
  // turk 3'e dusurulur; cay=3 secilirse turk 0'a dusurulur).
  const turkishMax = useMemo(() => getTurkishMax(potTea), [potTea]);
  useEffect(() => {
    if (Number(potCoffee) > turkishMax) {
      setPotCoffee(turkishMax);
    }
  }, [turkishMax, potCoffee]);

  // Degirmen sadece Turk Kahvesi gozu secili ise (potCoffee > 0) aktif
  // edilebilir; Turk Kahvesi sifirlanirsa hasGrinder de sifirlanir.
  useEffect(() => {
    if (Number(potCoffee) === 0 && hasGrinder) {
      setHasGrinder(false);
    }
  }, [potCoffee, hasGrinder]);

  useEffect(() => {
    if (open && pending) {
      setPotTea(0);
      setPotCoffee(0);
      setHasGrinder(false);
      setTeaPort(PORT_OPTIONS[0]);
      setTeaBaud(BAUD_OPTIONS.find((b) => b.id === 115200));
      setCoffeePort(PORT_OPTIONS[0]);
      setCoffeeBaud(BAUD_OPTIONS.find((b) => b.id === 19200));
    }
  }, [open, pending]);

  const handleSubmit = async () => {
    if (oldCardSet && !OKC_POT_OPTIONS.includes(Number(potCoffee))) {
      showError("Eski kart setinde Türk Kahvesi göz sayısı 2 ya da 4 olmalı.");
      return;
    }
    if (!machineName) {
      showError("En az bir göz sayısı (Çay / Filtre Kahve veya Türk Kahvesi) girilmeli.");
      return;
    }
    try {
      const result = await assign({
        token: auth?.accessToken,
        id: pending._id,
        machineName,
        identity: {
          potCountTeaFilterCoffee: Number(potTea) || 0,
          potCountTurkishCoffee: Number(potCoffee) || 0,
          hasGrinder: Number(potCoffee) > 0 ? !!hasGrinder : false,
          // Eski kart setinde tablet yok -> seri port da yok (#319).
          serComs: oldCardSet
            ? []
            : [
                {
                  machineType: "Çayset",
                  serCom: teaPort?.id,
                  baudRate: Number(teaBaud?.id),
                },
                {
                  machineType: "Fincanda",
                  serCom: coffeePort?.id,
                  baudRate: Number(coffeeBaud?.id),
                },
              ],
        },
      }).unwrap();
      showSuccess(`Cihaz oluşturuldu: ${result?.data?.serialNo}`);
      onSuccess?.(result);
      onClose();
    } catch (err) {
      showError(err?.data?.message || "Atama başarısız.");
    }
  };

  if (!mounted || !pending) return null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      position="right"
      width="md"
      title="Yeni Kurulum Onayı"
    >
      <Drawer.Body className="px-5 py-4">
        <div className="space-y-4">
          <div className="bg-input/30 rounded-lg p-3 text-sm">
            <div className="text-xs uppercase tracking-wider text-mainSecond/60">
              Doğrulama Kodu
            </div>
            <div className="font-mono font-bold text-2xl text-main mt-1">
              {pending.code}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-mainSecond/50 mt-2">
              Ön Kayıt ID
            </div>
            <div className="text-xs text-mainSecond/60 break-all font-mono">
              {pending.tempClientId}
            </div>
          </div>

          <div className="space-y-4">
            {!oldCardSet && (
              <PotCountSelector
                label="Çay / Filtre Kahve Göz Sayısı"
                value={potTea}
                onChange={setPotTea}
                options={[0, 1, 2, 3]}
              />
            )}
            <PotCountSelector
              label="Türk Kahvesi Göz Sayısı"
              value={potCoffee}
              onChange={setPotCoffee}
              options={oldCardSet ? OKC_POT_OPTIONS : [0, 1, 2, 3, 4, 5, 6]}
              disabledAfter={oldCardSet ? undefined : turkishMax}
            />
            {oldCardSet && (
              <div className="text-[11px] text-mainSecond/60">
                Eski kart setli makine: yalnız 2 ya da 4 göz.
              </div>
            )}
            {!oldCardSet && turkishMax < 6 && (
              <div className="text-[11px] text-mainSecond/60">
                {turkishMax === 0
                  ? "Çay / Filtre Kahve 3 göz seçildiği için Türk Kahvesi eklenemez."
                  : `Çay / Filtre Kahve ${potTea} göz seçildiği için Türk Kahvesi en fazla ${turkishMax} göz olabilir.`}
              </div>
            )}

            {/* Degirmen toggle — sadece Turk Kahvesi gozu (potCoffee > 0)
                varsa kullanilabilir. Seciliyse server hasGrinder=true
                gonderir; cihazda hem ayar (designInfo.settings.grinder
                Enabled) hem de deviceComponents.grinder.active=true
                yapilir. */}
            {Number(potCoffee) > 0 && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Değirmen
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setHasGrinder(true)}
                    className={`px-4 h-11 rounded-xl border text-sm font-semibold transition-all ${
                      hasGrinder
                        ? "border-main bg-main text-new shadow-sm"
                        : "border-input bg-white text-main hover:border-main/40 hover:shadow-sm"
                    }`}
                  >
                    Değirmenli
                  </button>
                  <button
                    type="button"
                    onClick={() => setHasGrinder(false)}
                    className={`px-4 h-11 rounded-xl border text-sm font-semibold transition-all ${
                      !hasGrinder
                        ? "border-main bg-main text-new shadow-sm"
                        : "border-input bg-white text-main hover:border-main/40 hover:shadow-sm"
                    }`}
                  >
                    Değirmensiz
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="bg-input/30 rounded-lg p-3 text-sm">
            <div className="text-xs uppercase tracking-wider text-mainSecond/60">
              Türetilen Makine Adı
            </div>
            <div className="font-medium text-main mt-1">
              {machineName || "—"}
            </div>
            <div className="text-[11px] text-mainSecond/60 mt-1">
              Cihaz üretim firmasında oluşturulacak; firmaya dağıtım ayrıca yapılır.
            </div>
          </div>

          {/* Eski kart setinde tablet yok -> seri port ayari yok (#319). */}
          {!oldCardSet && (
            <>
            <div className="border-t border-input pt-3">
              <div className="text-xs uppercase tracking-wider text-mainSecond/60 mb-2">
                Çayset Seri Port
              </div>
              <div className="grid grid-cols-2 gap-3">
                <CustomDropdown
                  label="Port"
                  value={teaPort}
                  onChange={setTeaPort}
                  options={PORT_OPTIONS}
                  showSearch={false}
                />
                <CustomDropdown
                  label="Baudrate"
                  value={teaBaud}
                  onChange={setTeaBaud}
                  options={BAUD_OPTIONS}
                  showSearch={false}
                />
              </div>
            </div>

            <div className="border-t border-input pt-3">
              <div className="text-xs uppercase tracking-wider text-mainSecond/60 mb-2">
                Fincanda Seri Port
              </div>
              <div className="grid grid-cols-2 gap-3">
                <CustomDropdown
                  label="Port"
                  value={coffeePort}
                  onChange={setCoffeePort}
                  options={PORT_OPTIONS}
                  showSearch={false}
                />
                <CustomDropdown
                  label="Baudrate"
                  value={coffeeBaud}
                  onChange={setCoffeeBaud}
                  options={BAUD_OPTIONS}
                  showSearch={false}
                />
              </div>
            </div>
            </>
          )}
        </div>
      </Drawer.Body>
      <Drawer.Footer className="border-t border-input px-5 py-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-lg border border-input text-mainSecond"
          disabled={isLoading}
        >
          Vazgeç
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading || !machineName}
          className="px-4 py-2 rounded-lg bg-main text-new font-medium hover:bg-mainSecond disabled:opacity-60"
        >
          {isLoading ? "Onaylanıyor..." : "Onayla"}
        </button>
      </Drawer.Footer>
    </Drawer>
  );
}
